import css from './ResetPasswordForm.module.css';

import TextInput from '../../Common/TextInput/TextInput';
import Validators from '../../../utility/validators';
import {
  DataStatus,
  ErrorCode,
  FormField,
  FormStatus,
} from '../../../types/appTypes';
import { useForm } from '../../../hooks/useForm';
import { useData } from '../../../hooks/useData';
import DataForm from '../../Common/DataForm/DataForm';
import React, { useContext, useState } from 'react';
import { UserContext } from '../../../context/userContext';
import { useNavigate } from 'react-router-dom';
import PasswordPolicy from '../PasswordPolicy/PasswordPolicy';
import { UserProfileSearchResult } from '../../../types/appModels';

type ResetPasswordFormProps = {
  token: string;
  tokenId: string;
  userId: string;
};

type ResetPasswordFormFields = {
  password: FormField<string>;
  copyPassword: FormField<string>;
};

const ResetPasswordForm = ({
  token,
  tokenId,
  userId,
}: ResetPasswordFormProps) => {
  const navigate = useNavigate();
  const { resetPassword } = useContext(UserContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fields, formState, setFields, setFormState } =
    useForm<ResetPasswordFormFields>({
      initialFields: {
        password: { value: '', valid: false },
        copyPassword: { value: '', valid: false },
      },
    });
  const { dataState } = useData<UserProfileSearchResult>({
    endpoint: `${process.env.REACT_APP_API_USER}/${encodeURIComponent(userId)}`,
    initialStatus: DataStatus.Loading,
    loadWhen: userId !== null && userId !== '',
  });

  const formIsValid = fields.password.valid && fields.copyPassword.valid;

  const formRequiredInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    const valid = Validators.required(value);
    setFields((pv) => {
      return { ...pv, [name]: { value: value, valid: valid } };
    });
  };

  const formSubmitHandler = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formIsValid) return;

    // Validate password match
    if (
      !Validators.valuesMatch(fields.password.value, fields.copyPassword.value)
    ) {
      setFormState({
        status: FormStatus.Fail,
        error: {
          code: ErrorCode.passwordMismatch,
          message: 'The passwords do not match',
        },
      });
      return;
    }

    // Validate password policy
    if (!Validators.passwordPolicy(fields.password.value)) {
      setFormState({
        status: FormStatus.Fail,
        error: {
          code: ErrorCode.passwordPolicyViolation,
          message: 'The password does not adhere to the policy',
        },
      });
      return;
    }

    setFormState({ status: FormStatus.Submitting });
    setIsSubmitting(true);

    try {
      // Call resetPassword with lowercase email for consistency
      const success = await resetPassword!(
        token, 
        tokenId, 
        userId, 
        fields.password.value
      );
      
      if (success) {
        setFormState({ status: FormStatus.Success });
      } else {
        setFormState({
          status: FormStatus.Fail,
          error: {
            code: ErrorCode.unknown,
            message:
              'An error occurred and your password could not be changed. The link may have expired. Please try submitting a forgot password request again.',
          },
        });
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setFormState({
        status: FormStatus.Fail,
        error: {
          code: ErrorCode.unknown,
          message:
            'An error occurred and your password could not be changed.',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const successActionHandler = (event: React.MouseEvent) => {
    navigate('/');
  };

  return (
    <DataForm
      title='RESET PASSWORD'
      successMessage='Your password has been changed. Click below to login.'
      successActionMessage='Login Now'
      successAction={successActionHandler}
      submitErrorMessage={formState.error?.message}
      dataLoadErrorMessage='An error occurred and the user cannot be found.'
      dataLoadNotFoundMessage='Invalid reset password data provided'
      secondaryContainerClass={css.container}
      status={formState.status}
      dataStatuses={[dataState.status]}
      onSubmit={formSubmitHandler}
    >
      <div className={css.instructions}>
        <p>Enter your new password below.</p>
      </div>
      <div className={css.passwordPolicy}>
        <PasswordPolicy />
      </div>
      <div className={css.formGroup}>
        <TextInput
          placeholder='Password'
          type='password'
          name='password'
          value={fields.password.value}
          valid={fields.password.valid}
          onChange={formRequiredInputChangeHandler}
          disabled={isSubmitting}
        />
        <TextInput
          placeholder='Confirm Password'
          type='password'
          name='copyPassword'
          value={fields.copyPassword.value}
          valid={fields.copyPassword.valid}
          onChange={formRequiredInputChangeHandler}
          disabled={isSubmitting}
        />
      </div>
      <div className={css.actions}>
        <button type='submit' disabled={!formIsValid || isSubmitting}>
          {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
        </button>
      </div>
    </DataForm>
  );
};

export default ResetPasswordForm;